import { DatePicker } from 'primeng/datepicker'

/**
 * Classe utilitaire pour la gestion des semaines dans le calendrier
 */
export class CalendarWeekTools {
  /**
   * Récupère la plage de dates de la semaine courante (lundi-dimanche)
   * @returns Un tuple [lundi, dimanche] de la semaine courante
   */
  public static getCurrentWeekRange(): [Date, Date] {
    const today = new Date()
    const currentWeekStart = new Date(today)
    const currentWeekEnd = new Date(today)

    // Ajuster au début de la semaine (lundi)
    const dayOfWeek = today.getDay() || 7 // transformer le dimanche (0) en 7
    currentWeekStart.setDate(today.getDate() - dayOfWeek + 1)

    // Ajuster à la fin de la semaine (dimanche)
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6)

    // Normaliser les heures
    currentWeekStart.setHours(0, 0, 0, 0)
    currentWeekEnd.setHours(23, 59, 59, 999)

    return [currentWeekStart, currentWeekEnd]
  }

  /**
   * Récupère la plage de dates de la semaine prochaine (lundi-dimanche)
   * @returns Un tuple [lundi, dimanche] de la semaine prochaine
   */
  public static getNextWeekRange(): [Date, Date] {
    const today = new Date()
    const nextWeekStart = new Date(today)
    const nextWeekEnd = new Date(today)

    // Ajuster au début de la prochaine semaine
    const dayOfWeek = today.getDay() || 7
    nextWeekStart.setDate(today.getDate() - dayOfWeek + 8) // +8 pour lundi prochain

    // Ajuster à la fin de la prochaine semaine
    nextWeekEnd.setDate(nextWeekStart.getDate() + 6)

    // Normaliser les heures
    nextWeekStart.setHours(0, 0, 0, 0)
    nextWeekEnd.setHours(23, 59, 59, 999)

    return [nextWeekStart, nextWeekEnd]
  }

  /**
   * Récupère la plage de dates des deux semaines à partir de cette semaine
   * @returns Un tuple [lundi cette semaine, dimanche semaine prochaine]
   */
  public static getTwoWeeksRange(): [Date, Date] {
    const today = new Date()
    const twoWeeksStart = new Date(today)
    const twoWeeksEnd = new Date(today)

    // Ajuster au début de cette semaine
    const dayOfWeek = today.getDay() || 7
    twoWeeksStart.setDate(today.getDate() - dayOfWeek + 1)

    // Ajuster à la fin de la semaine suivante (2 semaines au total)
    twoWeeksEnd.setDate(twoWeeksStart.getDate() + 13)

    // Normaliser les heures
    twoWeeksStart.setHours(0, 0, 0, 0)
    twoWeeksEnd.setHours(23, 59, 59, 999)

    return [twoWeeksStart, twoWeeksEnd]
  }

  /**
   * Gère le clic sur un numéro de semaine dans le calendrier
   * @param event L'événement de clic
   * @param weekPickerCalendar Le composant DatePicker
   * @returns La plage de dates sélectionnée ou null si non applicable
   */
  public static handleWeekNumberClick(
    event: MouseEvent,
    weekPickerCalendar: DatePicker | undefined
  ): [Date, Date] | null {
    const target = event.target as HTMLElement

    // Vérifier si l'élément cliqué est une cellule de numéro de semaine
    if (
      !target.classList.contains('p-datepicker-weeknumber') &&
      !target.closest('.p-datepicker-weeknumber')
    ) {
      return null
    }

    // Trouver la ligne qui a été cliquée
    const row = target.closest('tr')
    if (!row) {
      return null
    }

    // Récupérer le mois et l'année courants du DatePicker
    const viewMonth = weekPickerCalendar?.currentMonth ?? new Date().getMonth()
    const viewYear = weekPickerCalendar?.currentYear ?? new Date().getFullYear()

    // Récupérer toutes les cellules de jour dans la ligne (exclure la cellule de numéro de semaine)
    const dayCells = row.querySelectorAll('td:not(.p-datepicker-weeknumber)')

    if (dayCells.length !== 7) {
      return null
    }

    const weekDates: Date[] = []

    dayCells.forEach((cell, index) => {
      const span = cell.querySelector('span')
      if (span) {
        const dayNum = parseInt(span.textContent || '0', 10)
        if (dayNum > 0) {
          let cellMonth = viewMonth
          let cellYear = viewYear

          // Vérifier si cette cellule est d'un autre mois
          const isOtherMonth = cell.classList.contains(
            'p-datepicker-other-month'
          )

          if (isOtherMonth) {
            // Utiliser la position dans la ligne pour déterminer si c'est le mois précédent ou suivant
            if (index < 3 && dayNum > 20) {
              // Mois précédent
              cellMonth = viewMonth - 1
              if (cellMonth < 0) {
                cellMonth = 11
                cellYear = viewYear - 1
              }
            } else if (index > 3 && dayNum < 10) {
              // Mois suivant
              cellMonth = viewMonth + 1
              if (cellMonth > 11) {
                cellMonth = 0
                cellYear = viewYear + 1
              }
            }
          }

          weekDates.push(new Date(cellYear, cellMonth, dayNum))
        }
      }
    })

    if (weekDates.length === 0) {
      return null
    }

    // Récupérer la première et dernière date de la semaine
    const startDate = weekDates[0]
    const endDate = weekDates[weekDates.length - 1]

    // Normaliser les heures
    startDate.setHours(0, 0, 0, 0)
    endDate.setHours(23, 59, 59, 999)

    return [startDate, endDate]
  }

  /**
   * Vérifie si une plage de dates est valide
   * @param dates La plage de dates à vérifier
   * @returns true si la plage est valide
   */
  public static isValidDateRange(dates: Date[] | null): boolean {
    if (!dates || dates.length !== 2) return false
    const [start, end] = dates
    if (!start || !end) return false
    if (
      !(start instanceof Date) ||
      !(end instanceof Date) ||
      isNaN(start.getTime()) ||
      isNaN(end.getTime())
    ) {
      return false
    }
    // Vérifier que les dates ne sont pas en 1970 (timestamp proche de 0)
    if (start.getFullYear() < 2000 || end.getFullYear() < 2000) {
      return false
    }
    return true
  }

  /**
   * Normalise une plage de dates (début à 00:00:00, fin à 23:59:59)
   * @param dates La plage de dates à normaliser
   * @returns La plage normalisée
   */
  public static normalizeDateRange(dates: [Date, Date]): [Date, Date] {
    const startDate = new Date(dates[0])
    const endDate = new Date(dates[1])

    startDate.setHours(0, 0, 0, 0)
    endDate.setHours(23, 59, 59, 999)

    return [startDate, endDate]
  }
}
